import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import { MdFilterList, MdSearch, MdClose, MdCheckBox, MdCheckBoxOutlineBlank, MdTune } from 'react-icons/md';
import useHFData from './hooks/useHFData';
import OverviewPage from './OverviewPage';
import FundDetailPage from './FundDetailPage';
import ComparisonPage from './ComparisonPage';
import CorrelationPage from './CorrelationPage';
import DrawdownPage from './DrawdownPage';
import RollingPage from './RollingPage';
import CalendarPage from './CalendarPage';
import DataManagePage from './DataManagePage';
import { useAuth } from '../../context/AuthContext';
import { hfService } from '../../services/hfService';

// Sticky, config-driven default Focus Fund. The preferred fund is a BUILD-TIME CONFIG value
// (REACT_APP_DEFAULT_FOCUS_FUND) — never a hardcoded name in logic — so it generalises to any deployment.
// Rule: if the configured fund is present in the currently-loaded bucket, it stays selected across every
// strategy load; otherwise fall back to the first fund in that bucket. (Focus Fund only sets the default
// chart start date — it does NOT filter which funds are shown.)
// `preferred` is the user's saved default (UserProfile.hf_default_focus_fund, set via the ★ button),
// falling back to the build-time config. Prefer it when it's a member of the loaded bucket, else the
// first fund. Company-agnostic — the value is data/config, never hardcoded.
const ENV_DEFAULT_FOCUS = (process.env.REACT_APP_DEFAULT_FOCUS_FUND || '').trim();
const pickDefaultFocus = (funds, preferred) => {
  const list = funds || [];
  const pref = ((preferred || ENV_DEFAULT_FOCUS) || '').trim();
  if (pref) {
    const needle = pref.toLowerCase();
    const match = list.find(f => (f || '').toLowerCase().includes(needle));
    if (match) return match;
  }
  return list[0] || '';
};

const HFLayout = () => {
  const {
    datasets, activeDataset, selectedFunds, dateRange, rfRate, loading, error,
    setSelectedFunds, setDateRange, setRfRate, selectDataset, uploadDataset, getAnalyticsParams, cachedFetch, loadDatasets,
  } = useHFData();

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState('__multi_strat_pending__');
  const [focusFund, setFocusFund] = useState('');
  const [focusDropdownOpen, setFocusDropdownOpen] = useState(false);
  const [fundSearch, setFundSearch] = useState('');
  const [mobileRfOpen, setMobileRfOpen] = useState(false);
  const [mobileFocusOpen, setMobileFocusOpen] = useState(false);
  const [advFiltersOpen, setAdvFiltersOpen] = useState(false);
  const [advFilters, setAdvFilters] = useState({
    minMonths: '', maxVol: '', minSharpe: '', minReturn: '', maxDrawdown: '',
  });
  const fileRef = useRef(null);
  const focusDropdownRef = useRef(null);

  // Per-user default Focus Fund (the ★ Make Default button). Seeded from the user's saved profile
  // value, then threaded into pickDefaultFocus so it wins on every load when present in the bucket.
  const { user } = useAuth();
  const [savedDefault, setSavedDefault] = useState(user?.profile?.hf_default_focus_fund || '');
  useEffect(() => { if (user?.profile) setSavedDefault(user.profile.hf_default_focus_fund || ''); }, [user]);
  const makeDefault = (fund) => {
    setSavedDefault(fund);                                  // optimistic — applies to the next load
    hfService.setDefaultFocusFund(fund).catch(() => {});    // persist (best-effort)
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      await uploadDataset(file, file.name);
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const fundList = activeDataset?.funds || [];
  const allFundNames = fundList.map(f => f.display_name);

  // Derive unique strategies from fund list
  const strategies = React.useMemo(() => {
    const counts = {};
    fundList.forEach(f => {
      const strat = f.strategy || 'Uncategorized';
      counts[strat] = (counts[strat] || 0) + 1;
    });
    return Object.entries(counts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, count]) => ({ name, count }));
  }, [fundList]);

  // Strategy funds = all funds in the selected strategy (used for API calls)
  const strategyFunds = React.useMemo(() => {
    if (selectedStrategy === '__all__') return allFundNames;
    return fundList
      .filter(f => (f.strategy || 'Uncategorized') === selectedStrategy)
      .map(f => f.display_name);
  }, [selectedStrategy, fundList, allFundNames]);

  // Apply advanced filters to a fund list using pre-computed stats from the dataset detail
  const applyAdvFilters = (funds, filters) => {
    if (!filters || Object.values(filters).every(v => v === '' || v === null)) return funds;
    // Fund stats come from the dataset detail (fundList has basic info, we need stats from API)
    // For now, filter by month_count which is on the Fund model
    let result = [...funds];
    if (filters.minMonths) {
      const min = parseInt(filters.minMonths);
      const fundMonths = {};
      fundList.forEach(f => { fundMonths[f.display_name] = f.month_count || 0; });
      result = result.filter(f => (fundMonths[f] || 0) >= min);
    }
    return result;
  };

  // When strategy changes, update selectedFunds to all strategy funds + reset focus
  const handleStrategyChange = (stratValue) => {
    setSelectedStrategy(stratValue);
    let funds = stratValue === '__all__'
      ? allFundNames
      : fundList.filter(f => (f.strategy || 'Uncategorized') === stratValue).map(f => f.display_name);
    funds = applyAdvFilters(funds, advFilters);
    setSelectedFunds(funds);
    // pickDefaultFocus is company-agnostic: prefers the build-config fund (REACT_APP_DEFAULT_FOCUS_FUND)
    // when present in this bucket, else the first fund. No hardcoded name — see helper above.
    setFocusFund(pickDefaultFocus(funds, savedDefault));
  };

  // When advanced filters change, reapply to current strategy
  const handleAdvFilterChange = (key, value) => {
    const newFilters = { ...advFilters, [key]: value };
    setAdvFilters(newFilters);
    let funds = selectedStrategy === '__all__'
      ? allFundNames
      : fundList.filter(f => (f.strategy || 'Uncategorized') === selectedStrategy).map(f => f.display_name);
    funds = applyAdvFilters(funds, newFilters);
    setSelectedFunds(funds);
  };

  // Default to Multi-Strat + the configured Focus Fund on FIRST dataset load only
  const hasInitialized = React.useRef(false);
  React.useEffect(() => {
    if (strategies.length > 0 && !hasInitialized.current) {
      hasInitialized.current = true;
      const multiStrat = strategies.find(s => s.name === 'Multi-Strat') || strategies.find(s => s.name === 'Multi-Strategy') || strategies.find(s => s.name.toLowerCase().includes('multi'));
      if (multiStrat) {
        setSelectedStrategy(multiStrat.name);
        const funds = fundList
          .filter(f => (f.strategy || 'Uncategorized') === multiStrat.name)
          .map(f => f.display_name);
        setSelectedFunds(funds);
        // pickDefaultFocus is company-agnostic: prefers the build-config fund (REACT_APP_DEFAULT_FOCUS_FUND)
        // when present in this bucket, else the first fund. No hardcoded name — see helper above.
        setFocusFund(pickDefaultFocus(funds, savedDefault));
      } else {
        setSelectedStrategy('__all__');
        setSelectedFunds(allFundNames.slice(0, 10));
        setFocusFund(pickDefaultFocus(allFundNames, savedDefault));
      }
    }
  }, [activeDataset?.id, strategies.length]);

  // Close focus dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (focusDropdownRef.current && !focusDropdownRef.current.contains(e.target)) {
        setFocusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus fund search — searches across ALL funds (not just strategy)
  const filteredFocusFunds = fundSearch
    ? allFundNames.filter(f => f.toLowerCase().includes(fundSearch.toLowerCase()))
    : strategyFunds;

  // Memoize so child useEffects only fire when actual values change
  const fundsKey = JSON.stringify(selectedFunds);
  const filtersKey = JSON.stringify(advFilters);
  const analyticsParams = useMemo(() => ({
    ...getAnalyticsParams(),
    adv_filters: advFilters,
  }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeDataset?.id, fundsKey, dateRange.start, dateRange.end, rfRate, filtersKey]
  );

  if (loading && !activeDataset) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-brand-gold" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading datasets...</p>
      </div>
    );
  }

  const selectCls = 'bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold outline-none w-full';
  const labelCls = 'text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block';

  return (
    <div>
      {/* ===== MOBILE: Horizontal pill filters ===== */}
      <div className="md:hidden flex gap-2 overflow-x-auto pb-2 mb-2 scrollbar-thin">
        <select
          className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-0 outline-none appearance-none"
          value={activeDataset?.id || ''}
          onChange={(e) => selectDataset(Number(e.target.value))}
        >
          {datasets.map(ds => (
            <option key={ds.id} value={ds.id}>{ds.name}</option>
          ))}
        </select>
        <select
          className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border border-brand-gold text-brand-gold bg-transparent outline-none appearance-none"
          value={selectedStrategy}
          onChange={(e) => handleStrategyChange(e.target.value)}
        >
          <option value="__all__">All Strategies</option>
          {strategies.map(s => (
            <option key={s.name} value={s.name}>{s.name} ({s.count})</option>
          ))}
        </select>
        <button
          onClick={() => { setMobileRfOpen(!mobileRfOpen); setMobileFocusOpen(false); }}
          className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400"
        >
          Rf {(rfRate * 100).toFixed(1)}%
        </button>
        <button
          onClick={() => { setMobileFocusOpen(!mobileFocusOpen); setMobileRfOpen(false); setFundSearch(''); }}
          className="shrink-0 text-xs font-medium px-3 py-1.5 rounded-full border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 truncate max-w-[140px]"
        >
          Focus: {focusFund ? focusFund.split(' ')[0] : '—'}
        </button>
      </div>

      {/* Mobile Rf slider popup */}
      {mobileRfOpen && (
        <div className="md:hidden mx-1 mb-2 p-3 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Risk-Free Rate: {(rfRate * 100).toFixed(1)}%</span>
            <button onClick={() => setMobileRfOpen(false)} className="text-gray-400 text-sm">Done</button>
          </div>
          <input
            type="range" min="0" max="0.10" step="0.005" value={rfRate}
            onChange={(e) => setRfRate(Number(e.target.value))}
            className="w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-gray-300 dark:bg-gray-700 accent-brand-gold"
          />
        </div>
      )}

      {/* Mobile Focus Fund dropdown */}
      {mobileFocusOpen && (
        <div className="md:hidden mx-1 mb-2 bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg max-h-64 flex flex-col">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
            <MdSearch className="text-gray-400 shrink-0" />
            <input
              type="text" placeholder="Search funds..." value={fundSearch}
              onChange={(e) => setFundSearch(e.target.value)}
              className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-200 outline-none placeholder-gray-400"
              autoFocus
            />
            <button onClick={() => setMobileFocusOpen(false)} className="text-xs text-gray-400">Done</button>
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredFocusFunds.map(f => (
              <div
                key={f}
                onClick={() => { setFocusFund(f); setMobileFocusOpen(false); }}
                className={`px-3 py-2 text-sm truncate ${
                  f === focusFund ? 'bg-brand-gold/10 text-brand-gold font-medium' : 'text-gray-800 dark:text-gray-200 active:bg-gray-100 dark:active:bg-gray-800'
                }`}
              >{f}</div>
            ))}
          </div>
        </div>
      )}

      {/* ===== DESKTOP: Full filter grid ===== */}
      <div className="hidden md:block">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          {/* Dataset */}
          <div className="col-span-2 md:col-span-1">
            <label className={labelCls}>Dataset</label>
            <select
              className={selectCls}
              value={activeDataset?.id || ''}
              onChange={(e) => selectDataset(Number(e.target.value))}
            >
              {datasets.map(ds => (
                <option key={ds.id} value={ds.id}>{ds.name} ({ds.fund_count} funds)</option>
              ))}
            </select>
          </div>

          {/* Strategy */}
          <div>
            <label className={labelCls}>Strategy</label>
            <select
              className={selectCls}
              value={selectedStrategy}
              onChange={(e) => handleStrategyChange(e.target.value)}
            >
              <option value="__all__">All</option>
              {strategies.map(s => (
                <option key={s.name} value={s.name}>{s.name} ({s.count})</option>
              ))}
            </select>
          </div>

          {/* Rf Rate Slider */}
          <div>
            <label className={labelCls}>Rf Rate: {(rfRate * 100).toFixed(1)}%</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="range"
                min="0"
                max="0.10"
                step="0.005"
                value={rfRate}
                onChange={(e) => setRfRate(Number(e.target.value))}
                className="w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-gray-300 dark:bg-gray-700 accent-brand-gold"
              />
            </div>
          </div>

          {/* Focus Fund — single select, determines chart start date */}
          <div className="relative" ref={focusDropdownRef}>
            <label className={labelCls}>Focus Fund</label>
            <button
              type="button"
              onClick={() => { setFocusDropdownOpen(!focusDropdownOpen); setFundSearch(''); }}
              className={`${selectCls} text-left truncate`}
            >
              {focusFund || 'Select fund...'}
            </button>
            {focusDropdownOpen && (
              <div className="absolute z-50 mt-1 w-full min-w-[280px] bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-72 flex flex-col">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                  <MdSearch className="text-gray-400 shrink-0" />
                  <input
                    type="text"
                    placeholder="Search funds..."
                    value={fundSearch}
                    onChange={(e) => setFundSearch(e.target.value)}
                    className="flex-1 bg-transparent text-sm text-gray-800 dark:text-gray-200 outline-none placeholder-gray-400"
                    autoFocus
                  />
                  {fundSearch && (
                    <MdClose className="text-gray-400 cursor-pointer hover:text-gray-600" onClick={() => setFundSearch('')} />
                  )}
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredFocusFunds.map(f => (
                    <div
                      key={f}
                      className={`flex items-center gap-1 px-3 py-2 text-sm ${
                        f === focusFund ? 'bg-brand-gold/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                      }`}
                    >
                      <span
                        onClick={() => { setFocusFund(f); setFocusDropdownOpen(false); }}
                        className={`flex-1 cursor-pointer truncate ${
                          f === focusFund ? 'text-brand-gold font-medium' : 'text-gray-800 dark:text-gray-200'
                        }`}
                      >
                        {f}
                      </span>
                      <button
                        type="button"
                        title={f === savedDefault ? 'Your default focus fund' : 'Make this my default'}
                        onClick={(e) => { e.stopPropagation(); makeDefault(f); }}
                        className={`shrink-0 px-1 text-base leading-none ${
                          f === savedDefault ? 'text-brand-gold' : 'text-gray-300 dark:text-gray-600 hover:text-brand-gold'
                        }`}
                      >
                        {f === savedDefault ? '★' : '☆'}
                      </button>
                    </div>
                  ))}
                  {filteredFocusFunds.length === 0 && (
                    <div className="px-3 py-3 text-sm text-gray-400 text-center">No funds match "{fundSearch}"</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Upload */}
          <div>
            <label className={labelCls}>Upload</label>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleUpload}
              disabled={uploading}
              className="block w-full text-sm text-gray-800 dark:text-gray-200 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-gray-200 dark:file:border-gray-700 file:text-sm file:bg-white dark:file:bg-[#0d1117] file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-50 dark:hover:file:bg-gray-800 cursor-pointer"
            />
          </div>
        </div>

        {/* Advanced Filters toggle + panel */}
        <div className="mb-1">
          <button
            onClick={() => setAdvFiltersOpen(!advFiltersOpen)}
            className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-brand-gold transition-colors"
          >
            <MdTune size={14} />
            Advanced Filters
            {Object.values(advFilters).some(v => v !== '') && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-brand-gold/10 text-brand-gold text-[10px] font-medium">
                {Object.values(advFilters).filter(v => v !== '').length} active
              </span>
            )}
          </button>
          {advFiltersOpen && (
            <div className="grid grid-cols-5 gap-2 mt-2 p-3 bg-gray-50 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Min Months</label>
                <input type="number" placeholder="e.g. 60" value={advFilters.minMonths}
                  onChange={e => handleAdvFilterChange('minMonths', e.target.value)}
                  className={`${selectCls} text-xs`} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Max Vol (Ann.%)</label>
                <input type="number" step="0.01" placeholder="e.g. 15" value={advFilters.maxVol}
                  onChange={e => handleAdvFilterChange('maxVol', e.target.value)}
                  className={`${selectCls} text-xs`} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Min Sharpe</label>
                <input type="number" step="0.01" placeholder="e.g. 0.5" value={advFilters.minSharpe}
                  onChange={e => handleAdvFilterChange('minSharpe', e.target.value)}
                  className={`${selectCls} text-xs`} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Min Ann. Return %</label>
                <input type="number" step="0.01" placeholder="e.g. 5" value={advFilters.minReturn}
                  onChange={e => handleAdvFilterChange('minReturn', e.target.value)}
                  className={`${selectCls} text-xs`} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1 block">Max Drawdown %</label>
                <input type="number" step="0.01" placeholder="e.g. -20" value={advFilters.maxDrawdown}
                  onChange={e => handleAdvFilterChange('maxDrawdown', e.target.value)}
                  className={`${selectCls} text-xs`} />
              </div>
              {Object.values(advFilters).some(v => v !== '') && (
                <div className="col-span-5 flex items-center justify-between">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {selectedFunds.length} funds match filters
                  </span>
                  <button
                    onClick={() => { setAdvFilters({ minMonths: '', maxVol: '', minSharpe: '', minReturn: '', maxDrawdown: '' }); handleAdvFilterChange('minMonths', ''); }}
                    className="text-xs text-red-500 hover:text-red-700"
                  >Clear All</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Alerts */}
      {uploadError && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-2.5 text-sm text-red-700 dark:text-red-400">
          <span>{uploadError}</span>
          <button onClick={() => setUploadError(null)} className="ml-2 text-red-500 hover:text-red-700 dark:hover:text-red-300 font-bold">&times;</button>
        </div>
      )}
      {uploading && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-2.5 text-sm text-blue-700 dark:text-blue-400">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-300 border-t-blue-600" />
          Uploading and parsing...
        </div>
      )}
      {error && (
        <div className="mb-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-2.5 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Navigation tabs */}
      <nav className="flex overflow-x-auto gap-1 border-b border-gray-200 dark:border-gray-800 mb-4">
        {[
          { to: '/hf', label: 'Overview', end: true },
          { to: '/hf/fund-detail', label: 'Fund Detail' },
          { to: '/hf/comparison', label: 'Comparison' },
          { to: '/hf/correlation', label: 'Correlation' },
          { to: '/hf/drawdown', label: 'Drawdown' },
          { to: '/hf/rolling', label: 'Rolling' },
          { to: '/hf/calendar', label: 'Calendar' },
          { to: '/hf/data', label: 'Data' },
        ].map(tab => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              `px-3 py-2 text-sm whitespace-nowrap transition-colors ${
                isActive
                  ? 'border-b-2 border-brand-gold text-brand-gold'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`
            }
          >
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {/* Pages */}
      <Routes>
        <Route index element={<OverviewPage analyticsParams={analyticsParams} focusFund={focusFund} cachedFetch={cachedFetch} />} />
        <Route path="fund-detail" element={<FundDetailPage analyticsParams={analyticsParams} fundList={fundList.filter(f => selectedStrategy === '__all__' || (f.strategy || 'Uncategorized') === selectedStrategy)} focusFund={focusFund} cachedFetch={cachedFetch} />} />
        <Route path="comparison" element={<ComparisonPage analyticsParams={analyticsParams} fundList={fundList} focusFund={focusFund} cachedFetch={cachedFetch} />} />
        <Route path="correlation" element={<CorrelationPage analyticsParams={analyticsParams} focusFund={focusFund} cachedFetch={cachedFetch} />} />
        <Route path="drawdown" element={<DrawdownPage analyticsParams={analyticsParams} focusFund={focusFund} cachedFetch={cachedFetch} />} />
        <Route path="rolling" element={<RollingPage analyticsParams={analyticsParams} focusFund={focusFund} cachedFetch={cachedFetch} />} />
        <Route path="calendar" element={<CalendarPage analyticsParams={analyticsParams} fundList={fundList} focusFund={focusFund} cachedFetch={cachedFetch} />} />
        <Route path="data" element={<DataManagePage analyticsParams={analyticsParams} fundList={fundList} onDataChanged={() => { loadDatasets(); }} />} />
      </Routes>
    </div>
  );
};

export default HFLayout;
